import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ArrowRight, Lock, Mail, User, Phone, KeyRound, ShieldCheck } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register, requestPasswordReset } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [resolvedInvite, setResolvedInvite] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Debounced access-code resolver → shows the mentorship name before signup.
  useEffect(() => {
    if (mode !== "register") return undefined;
    if (!accessCode || accessCode.trim().length < 3) {
      setResolvedInvite(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setCheckingCode(true);
      try {
        const res = await api.get(`/api/invites/lookup?code=${encodeURIComponent(accessCode.trim())}`);
        setResolvedInvite(res.data);
      } catch (err) {
        setResolvedInvite({ error: err?.response?.data?.detail || "Código inválido." });
      } finally {
        setCheckingCode(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [accessCode, mode]);

  const translateSupabaseError = (msg) => {
    if (!msg) return "Erro de autenticação. Verifique os dados.";
    const m = msg.toLowerCase();
    if (m.includes("invalid login") || m.includes("invalid credentials"))
      return "E-mail ou senha incorretos.";
    if (m.includes("email not confirmed"))
      return "Confirme seu e-mail antes de entrar.";
    if (m.includes("user already registered") || m.includes("already been registered"))
      return "Este e-mail já está cadastrado. Use a aba Entrar.";
    if (m.includes("password") && m.includes("6"))
      return "A senha precisa ter no mínimo 6 caracteres.";
    return msg;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    try {
      if (mode === "login") {
        await login(email, password);
      } else if (mode === "register") {
        if (password !== passwordConfirm) {
          setError("As senhas não coincidem.");
          setLoading(false);
          return;
        }
        if (!resolvedInvite || resolvedInvite.error) {
          setError("Informe um código de mentoria válido para se cadastrar.");
          setLoading(false);
          return;
        }
        const trimmedCode = accessCode.trim().toUpperCase();
        const res = await register(name, email, password, phone);
        // Attempt to claim the invite (works if session is now active).
        if (res?.session) {
          try {
            const claim = await api.post("/api/invites/claim", { access_code: trimmedCode });
            // Beta free access: always activate so student can enter.
            if (claim.data?.status !== "enrolled_waived") {
              try {
                await api.post("/api/onboarding/beta-activate");
              } catch (activateErr) {
                // Non-fatal: free-tier activation is already permitted
                // by the invite claim; surface for ops but don't block.
                console.warn("beta-activate failed (ignored):", activateErr);
              }
            }
            window.location.href = "/";
            return;
          } catch (claimErr) {
            setInfo("Cadastro realizado, mas não foi possível aplicar o código automaticamente. Faça login para continuar.");
          }
        } else {
          setInfo("Cadastro realizado. Verifique seu e-mail para confirmar antes de entrar.");
          setMode("login");
        }
      } else if (mode === "forgot") {
        await requestPasswordReset(email);
        setInfo("Se este e-mail existir, enviamos um link para redefinir sua senha.");
        setMode("login");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Auth error:", err);
      setError(translateSupabaseError(err?.message || err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="auth-page-container"
      className="min-h-screen bg-[#0B0C10] flex flex-col justify-center py-10 px-4 sm:px-6 lg:px-8"
    >
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B00] to-[#E05000] flex items-center justify-center font-extrabold text-black text-2xl mx-auto shadow-[0_0_25px_rgba(255,107,0,0.5)] mb-3">
          C
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          MODO <span className="text-[#FF6B00]">CAVERNA</span>
        </h1>
        <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest font-semibold">
          Sistema Operacional de Escala & Mentoria Guiada
        </p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md space-y-4">
        <div className="bg-[#14161C] border border-[#282D3D] rounded-2xl p-6 shadow-2xl">
          {mode !== "forgot" && (
            <div className="flex border-b border-[#282D3D] mb-5">
              <button
                type="button"
                data-testid="toggle-login-tab"
                onClick={() => { setMode("login"); setError(""); setInfo(""); }}
                className={`flex-1 pb-2 text-xs font-bold transition-colors ${
                  mode === "login" ? "text-[#FF6B00] border-b-2 border-[#FF6B00]" : "text-gray-400"
                }`}
              >
                Entrar na Conta
              </button>
              <button
                type="button"
                data-testid="toggle-register-tab"
                onClick={() => { setMode("register"); setError(""); setInfo(""); }}
                className={`flex-1 pb-2 text-xs font-bold transition-colors ${
                  mode === "register" ? "text-[#FF6B00] border-b-2 border-[#FF6B00]" : "text-gray-400"
                }`}
              >
                Criar Conta de Aluno
              </button>
            </div>
          )}

          {mode === "forgot" && (
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="w-4 h-4 text-[#FF6B00]" />
              <span className="text-sm font-bold text-white">Redefinir Senha</span>
            </div>
          )}

          {error && (
            <div
              data-testid="auth-error-alert"
              className="mb-4 p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-xs text-red-300"
            >
              {error}
            </div>
          )}
          {info && (
            <div
              data-testid="auth-info-alert"
              className="mb-4 p-3 bg-green-500/10 border border-green-500/40 rounded-lg text-xs text-green-300"
            >
              {info}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Nome Completo</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      data-testid="register-name-input"
                      required
                      placeholder="Gabriel Affiliado"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">WhatsApp</label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      data-testid="register-phone-input"
                      placeholder="(11) 99999-9999"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">E-mail</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                <input
                  type="email"
                  data-testid="auth-email-input"
                  required
                  placeholder="aluno@modocaverna.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                />
              </div>
            </div>

            {mode !== "forgot" && (
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Senha</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                  <input
                    type="password"
                    data-testid="auth-password-input"
                    required
                    minLength={6}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                  />
                </div>
              </div>
            )}

            {mode === "register" && (
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Confirmar Senha</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                  <input
                    type="password"
                    data-testid="auth-password-confirm-input"
                    required
                    minLength={6}
                    placeholder="Repita a senha"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className={`w-full bg-[#1A1D26] border rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none ${
                      passwordConfirm && passwordConfirm !== password
                        ? "border-red-500 focus:border-red-500"
                        : "border-[#282D3D] focus:border-[#FF6B00]"
                    }`}
                  />
                </div>
                {passwordConfirm && passwordConfirm !== password && (
                  <p className="text-[10px] text-red-400 mt-1">As senhas não coincidem.</p>
                )}
              </div>
            )}

            {mode === "register" && (
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Código da Mentoria</label>
                <div className="relative">
                  <ShieldCheck className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    data-testid="auth-access-code-input"
                    required
                    placeholder="Ex: CAVERNA2026"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    className={`w-full bg-[#1A1D26] border rounded-lg py-2 pl-9 pr-3 text-white text-xs uppercase tracking-widest focus:outline-none ${
                      resolvedInvite?.error
                        ? "border-red-500 focus:border-red-500"
                        : resolvedInvite
                        ? "border-emerald-500 focus:border-emerald-500"
                        : "border-[#282D3D] focus:border-[#FF6B00]"
                    }`}
                  />
                </div>
                {checkingCode && (
                  <p className="text-[10px] text-gray-400 mt-1">Verificando código...</p>
                )}
                {!checkingCode && resolvedInvite && !resolvedInvite.error && (
                  <p
                    data-testid="auth-access-code-mentorship"
                    className="text-[10px] text-emerald-400 mt-1"
                  >
                    Mentoria: <strong>{resolvedInvite.mentorship_name}</strong>
                  </p>
                )}
                {!checkingCode && resolvedInvite?.error && (
                  <p className="text-[10px] text-red-400 mt-1">{resolvedInvite.error}</p>
                )}
                <p className="text-[10px] text-gray-500 mt-1">
                  Solicite o código ao seu mentor. Sem código não é possível se cadastrar.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(255,107,0,0.4)] flex items-center justify-center gap-2 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span>
                {loading
                  ? "Processando..."
                  : mode === "register"
                  ? "Criar Minha Conta"
                  : mode === "forgot"
                  ? "Enviar Link de Recuperação"
                  : "Entrar na Caverna"}
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-3 text-center text-[11px]">
            {mode === "login" ? (
              <button
                type="button"
                data-testid="forgot-password-btn"
                onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                className="text-gray-400 hover:text-[#FF6B00] transition-colors"
              >
                Esqueci minha senha
              </button>
            ) : mode === "forgot" ? (
              <button
                type="button"
                data-testid="back-to-login-btn"
                onClick={() => { setMode("login"); setError(""); setInfo(""); }}
                className="text-gray-400 hover:text-[#FF6B00] transition-colors"
              >
                ← Voltar para Entrar
              </button>
            ) : null}
          </div>
        </div>

        {/* Mentor signup CTA */}
        <div className="text-center">
          <a
            href="/mentor-signup"
            data-testid="mentor-signup-link"
            className="text-[11px] text-gray-400 hover:text-[#FF6B00] transition-colors underline"
          >
            Sou mentor — quero criar minha mentoria
          </a>
        </div>

      </div>
    </div>
  );
}
